'use client'

import type { Session } from 'next-auth'
import { createContext, useContext, useEffect, useState } from 'react'
import { PermissionsProvider } from './PermissionsContext'
import LogRocket from 'logrocket'
import { fetchUser } from '../apiClient/user'
import { UserModel } from '@prisma/client'
import { showNotification } from '../utils/notifications'

type UserContextInterface = {
  user: UserModel | null
  pinnedTemplateIds: Set<number>
  setPinnedTemplateIds: React.Dispatch<React.SetStateAction<Set<number>>>
}

const UserContext = createContext<UserContextInterface>({
  user: {} as UserModel,
  pinnedTemplateIds: new Set(),
  setPinnedTemplateIds: () => undefined,
})

function useUserContext(): UserContextInterface {
  return useContext(UserContext)
}

export default function UserProvider({ session, children }: { session: Session | null; children: React.ReactNode }) {
  const [fetchedUser, setFetchedUser] = useState<UserModel | null>(null)
  const [pinnedTemplateIds, setPinnedTemplateIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    async function callFetchUser() {
      if (!session?.user) return

      const { data: response, error } = await fetchUser({
        uuid: session.user.uuid,
      })

      if (error) {
        showNotification({
          type: 'error',
          error,
        })
      }
      if (response) {
        setFetchedUser(response.user)
        setPinnedTemplateIds(new Set(response.pinnedTemplateIds))
      }
    }

    callFetchUser()
  }, [session?.user])

  useEffect(() => {
    if (session?.user && process.env.NEXT_PUBLIC_LOGROCKET_KEY) {
      LogRocket.init(process.env.NEXT_PUBLIC_LOGROCKET_KEY)
      LogRocket.identify(session.user.uuid, {
        name: session.user.name,
        email: session.user.email,
      })
    }
  }, [session?.user])

  return (
    <UserContext.Provider value={{ user: fetchedUser, pinnedTemplateIds, setPinnedTemplateIds }}>
      <PermissionsProvider pvfs={session?.user?.pvfs || []}>{children}</PermissionsProvider>
    </UserContext.Provider>
  )
}

export { UserProvider, useUserContext }
